import React, { useMemo } from "react";
import VStack from "../../../ui_atoms/VStack";
import { MockedProvider } from "@apollo/client/testing";
import type { Meta, StoryObj } from "@storybook/react";
import CreateMissionStack from "../../../navigations/CreateMissionStack";
import {
    buildActivityType,
    buildClient,
    buildClientInfo,
    buildClientProfile,
    buildIjmGroup,
    buildMission,
    buildSalaryForecast,
    buildUser,
    buildWorkerInfo,
    CCTS_FIELD_VALUES,
    CCTS_FIELDS,
    CCTS_WITH_LSE,
    IJM_CHOICES_MOCK,
} from "../../factories";
import { GET_CLIENT_AND_INVITATION_WORKERS } from "../../../graphql/client";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import {
    EarningType,
    GetSalaryDetailsQuery,
    ProfileType,
    WorkerInfo,
} from "../../../__generated__/graphql";
import { FIND_ACTIVITY_TYPE, CREATE_MISSION, GET_MISSION } from "../../../graphql/core";
import {
    FIND_CCT,
    GET_CCT_BY_EDITION_BY,
    GET_CCT_DETAILS_FIELD_VALUES,
    GET_CCT_DETAILS_FIELDS,
} from "../../../graphql/cct";
import { GET_SALARY_FORECAST } from "../../../graphql/salaries";
import { buildGraphqlMock_GET_SALARY_DETAILS } from "../../mock-factories";
import {
    CardStyleInterpolators,
    createStackNavigator,
    HeaderStyleInterpolators,
} from "@react-navigation/stack";
import SalaryCountingDetailsScreen from "../../../screens/SalaryCountingDetails";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import IJMEditorNavScreen from "../../../screens/client/IJMEditorNavScreen";

type StoryStackParams = {
    CreateMissionStack: { workerInfo?: WorkerInfo } | undefined;
    SalaryCountingDetails: {
        earningType: EarningType;
        salaryDetails: Pick<
            GetSalaryDetailsQuery["getSalaryDetails"],
            | "workerDeductions"
            | "invoiceSalary"
            | "netSalary"
            | "lseMargin"
            | "grossSalary"
            | "vacationCompensation"
            | "publicHolidaysCompensation"
            | "thirteenMonthCompensation"
            | "baseSalary"
            | "clientDeductions"
        >;
    };
    IJMEditorNavScreen: undefined;
};

const Stack = createStackNavigator<StoryStackParams>();
function StoryComponent() {
    const user = useMemo(
        () =>
            buildUser({
                clientInfos: [buildClientInfo({ id: "client" })],
                clientProfiles: [
                    buildClientProfile({
                        id: "client",
                        client: buildClient({
                            ijmGroup: buildIjmGroup({
                                id: "group-1",
                            }),
                            ijmPayFirstDays: true,
                            ijmPayRemainingPercents: 10,
                        }),
                    }),
                ],
            }),
        [],
    );
    const workerInfo = useMemo(buildWorkerInfo, []);
    const mission = useMemo(() => buildMission({}), []);

    const mocks = useMemo(
        () => [
            {
                variableMatcher: () => true,
                request: {
                    query: FIND_ACTIVITY_TYPE,
                },
                newData: () => ({
                    data: {
                        findActivityType: {
                            pageInfo: {
                                totalElements: 1,
                                hasNextPage: false,
                                hasPreviousPage: false,
                                startCursor: "fake-start-cursor",
                                endCursor: "fake-end-cursor",
                            },
                            elements: [buildActivityType()],
                        },
                    },
                }),
            },
            {
                variableMatcher: () => true,
                request: {
                    query: GET_CLIENT_AND_INVITATION_WORKERS,
                },
                newData: () => ({
                    data: {
                        getClientWorkers: [
                            buildWorkerInfo({
                                id: "worker-profile-1",
                            }),
                            workerInfo,
                        ],
                        allInvitations: {
                            __typename: "InvitationPagination",
                            count: 0,
                            elements: [],
                        },
                    },
                }),
            },
            {
                request: {
                    query: FIND_CCT,
                },
                variableMatcher: () => true,
                newData: () => {
                    return {
                        data: {
                            findCct: {
                                elements: Object.values(CCTS_WITH_LSE),
                                pageInfo: {
                                    totalElements: CCTS_WITH_LSE.length,
                                },
                            },
                        },
                    };
                },
            },
            {
                request: {
                    query: GET_CCT_BY_EDITION_BY,
                },
                variableMatcher: () => true,
                newData: ({ cctEditionId }: { cctEditionId: string }) => ({
                    data: {
                        getCctByEditionId: CCTS_WITH_LSE[cctEditionId],
                    },
                }),
            },
            {
                request: {
                    query: GET_CCT_DETAILS_FIELDS,
                },
                variableMatcher: () => true,

                newData: ({ cctEditionId }: { cctEditionId: string }) => {
                    return {
                        data: {
                            getCctDetailsFields: CCTS_FIELDS[cctEditionId],
                        },
                    };
                },
            },
            {
                request: {
                    query: GET_CCT_DETAILS_FIELD_VALUES,
                },
                variableMatcher: () => true,
                newData: ({
                    cctEditionId,
                    criteriaStructureId,
                }: {
                    cctEditionId: string;
                    criteriaStructureId: number;
                }) => {
                    const key = `${cctEditionId}-${criteriaStructureId}`;
                    return {
                        data: {
                            getCctDetailsFieldValues: CCTS_FIELD_VALUES[key],
                        },
                    };
                },
            },
            buildGraphqlMock_GET_SALARY_DETAILS(),
            {
                request: {
                    query: GET_SALARY_FORECAST,
                },
                variableMatcher: () => true,
                newData: () => ({
                    data: { getSalaryForecast: [buildSalaryForecast()] },
                }),
            },
            {
                request: {
                    query: CREATE_MISSION,
                },
                variableMatcher: () => true,
                newData: (props: Record<string, string | number | null | object>) => {
                    // eslint-disable-next-line no-console
                    console.log("createMission ->\n", JSON.stringify(props, null, 2));
                    return {
                        data: { createMission: mission },
                    };
                },
            },
            {
                request: {
                    query: GET_MISSION,
                },
                variableMatcher: () => true,
                newData: () => ({
                    data: { getMission: mission },
                }),
            },
            IJM_CHOICES_MOCK,
        ],
        [mission, workerInfo],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager
                profileType={ProfileType.Client}
                profileId={ProfileType.Client}
            >
                <MockedProvider mocks={mocks}>
                    <VStack
                        gap={1}
                        style={{ alignItems: "stretch", alignSelf: "stretch", flex: 1 }}
                    >
                        <Stack.Navigator initialRouteName="CreateMissionStack">
                            <Stack.Screen
                                name="CreateMissionStack"
                                component={CreateMissionStack}
                                initialParams={{ workerInfo }}
                                options={{
                                    headerShown: false,
                                    gestureEnabled: false,
                                    headerStyleInterpolator: HeaderStyleInterpolators.forSlideUp,
                                }}
                            />
                            <Stack.Screen
                                name="SalaryCountingDetails"
                                component={SalaryCountingDetailsScreen}
                                options={{
                                    headerShown: false,
                                    gestureEnabled: true,
                                    cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS,
                                }}
                            />
                            <Stack.Screen
                                name="IJMEditorNavScreen"
                                component={IJMEditorNavScreen}
                                options={{
                                    headerShown: false,
                                    gestureEnabled: true,
                                    animation: "slide_from_bottom",
                                }}
                            />
                        </Stack.Navigator>
                    </VStack>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
}

const meta = {
    title: "CreateMission/FULL",
    component: StoryComponent,
    argTypes: {},
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {};
